/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { listEconomicCalendar, fetchEconomicEventHistory } from "../../api/api";
import CalendarForecastValue from "./CalendarForecastValue";
import Widget from "../dashboard-widgets/Widget";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Modal } from "../ui/modal";
import { RefreshCw } from "lucide-react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

// --- Helpers ---
const DEFAULT_CURRENCIES = ["USD", "JPY", "EUR", "AUD", "CAD", "CHF", "GBP"];
const DEFAULT_IMPACTS = ["Medium", "High"];
const API_PAGE_SIZE = 500;

function getCurrentWeekRange() {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

function buildParams({ dateFrom, dateTo, currencies, impacts, search }) {
  const params = new URLSearchParams();
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  if (currencies?.length) params.append("currency", currencies.join(","));
  if (impacts?.length) params.append("impact", impacts.join(","));
  if (search) {
    params.append("search", search);
    params.append("event", search);
  }
  return params;
}

function getEventName(event) {
  const label = [
    event?.event,
    event?.event_name,
    event?.indicator,
    event?.title,
    event?.name,
  ].find((value) => typeof value === "string" && value.trim());

  return label ? label.trim() : "-";
}

async function fetchAllEvents(baseParams) {
  const firstParams = new URLSearchParams(baseParams);
  firstParams.set("page", "1");
  firstParams.set("page_size", String(API_PAGE_SIZE));

  const firstResponse = await listEconomicCalendar(firstParams);
  if (Array.isArray(firstResponse)) {
    return firstResponse;
  }

  const firstItems = Array.isArray(firstResponse?.results) ? firstResponse.results : [];
  const totalCount = typeof firstResponse?.count === "number" ? firstResponse.count : firstItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / API_PAGE_SIZE));

  if (totalPages === 1) {
    return firstItems;
  }

  const remainingPages = [];
  for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
    const pageParams = new URLSearchParams(baseParams);
    pageParams.set("page", String(pageNumber));
    pageParams.set("page_size", String(API_PAGE_SIZE));
    remainingPages.push(listEconomicCalendar(pageParams));
  }

  const responses = await Promise.all(remainingPages);
  return responses.reduce((allItems, response) => {
    const pageItems = Array.isArray(response?.results) ? response.results : Array.isArray(response) ? response : [];
    return allItems.concat(pageItems);
  }, firstItems);
}

function parseEconomicNumber(raw) {
  if (raw === null || raw === undefined) return NaN;
  if (typeof raw === 'number') return raw;
  const s = String(raw).trim();
  if (s === '' || s.toLowerCase() === 'n/a') return NaN;
  const match = s.match(/^(-?\d+(?:[\.,]\d+)?)([kKmMbB%]?)$/);
  if (!match) {
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }
  let num = parseFloat(match[1].replace(',', '.'));
  const suf = match[2].toLowerCase();
  if (suf === 'k') num *= 1e3;
  if (suf === 'm') num *= 1e6;
  if (suf === 'b') num *= 1e9;
  return num;
}

function last12MonthsRange() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 12);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

function formatDateTimeUtc(ts) {
  try {
    const d = new Date(ts);
    const mon = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${mon}/${day} ${hh}:${mm}`;
  } catch {
    return ts;
  }
}

function fmtVal(raw) {
  if (raw === null || raw === undefined || raw === '') return '-';
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', ''));
  if (!Number.isFinite(n)) return raw;
  const abs = Math.abs(n);
  if (abs >= 1e12) return (n / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (abs >= 1e4) return (n / 1e3).toFixed(1) + 'K';
  return String(raw);
}

const IMPACT_META = {
  Low: {
    color: 'var(--color-info)',
    badgeClassName: 'border border-info/25 bg-info/10 text-info',
  },
  Medium: {
    color: 'var(--color-warning)',
    badgeClassName: 'border border-warning/25 bg-warning/10 text-warning',
  },
  High: {
    color: 'var(--color-destructive)',
    badgeClassName: 'border border-destructive/25 bg-destructive/10 text-destructive',
  },
  default: {
    color: 'var(--color-text-secondary)',
    badgeClassName: 'border widget-muted-border bg-backgroundSurface/35 text-textSecondary',
  },
};

// --- Widget ---
export default function EconomicCalendarWidget({
  title = "Economic Calendar",
  initialDateFrom,
  initialDateTo,
  defaultCurrencies = DEFAULT_CURRENCIES,
  defaultImpacts = DEFAULT_IMPACTS,
  defaultSearch = "",
  limit = 20,
  showFilters = true,
  compact = true,
  autoRefreshMs = 0,
  showChart = true,
  showViewAllLink = true,
  hideTitle = false,
}) {
  const week = useMemo(() => getCurrentWeekRange(), []);
  const [dateFrom, setDateFrom] = useState(initialDateFrom || week.from);
  const [dateTo, setDateTo] = useState(initialDateTo || week.to);
  const [currencies, setCurrencies] = useState(defaultCurrencies);
  const [impacts, setImpacts] = useState(defaultImpacts);
  const [search, setSearch] = useState(defaultSearch);
  const pageSize = limit > 0 ? limit : 20;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [page, setPage] = useState(1);

  // Chart modal
  const [chartOpen, setChartOpen] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [chartTitle, setChartTitle] = useState("");
  const [chartData, setChartData] = useState([]);

  const timerRef = useRef(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = buildParams({ dateFrom, dateTo, currencies, impacts, search });
      const items = await fetchAllEvents(params);
      const now = new Date().toISOString();
      // upcoming events first (nearest to now at top), then past events descending
      const sorted = [...items].sort((a, b) => {
        const aTime = a.event_time || "";
        const bTime = b.event_time || "";
        const aFuture = aTime >= now;
        const bFuture = bTime >= now;
        if (aFuture && !bFuture) return -1;
        if (!aFuture && bFuture) return 1;
        if (aTime === bTime) return 0;
        if (aFuture) return aTime < bTime ? -1 : 1; // upcoming: soonest first
        return aTime > bTime ? -1 : 1; // past: most recent first
      });
      setAllEvents(sorted);
    } catch (e) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoRefreshMs || autoRefreshMs <= 0) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetchData, autoRefreshMs);
    return () => timerRef.current && clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshMs, dateFrom, dateTo, currencies, impacts, search]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, currencies, impacts, search]);

  const totalCount = allEvents.length;
  const totalPages = limit > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
  const pageStart = limit > 0 ? (page - 1) * pageSize : 0;
  const pageEnd = limit > 0 ? pageStart + pageSize : totalCount;
  const events = limit > 0 ? allEvents.slice(pageStart, pageEnd) : allEvents;

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  const openChart = async (eventName, currency, indicatorId) => {
    if (!showChart) return;
    setChartOpen(true);
    setChartLoading(true);
    setChartError(null);
    setChartTitle(`${eventName} (${currency})`);
    try {
      const { from, to } = last12MonthsRange();
      const res = await fetchEconomicEventHistory(eventName, currency, from, to, indicatorId);
      const rows = Array.isArray(res) ? res : res?.results || res?.data?.results || res?.data || [];
      const points = rows
        .filter(r => (indicatorId ? r.metadata?.mt5_calendar?.event_id === indicatorId : r.event === eventName) && r.currency === currency)
        .map(r => ({
          date: r.event_time ? r.event_time.slice(0, 10) : r.date || "",
          actual: parseEconomicNumber(r.actual),
          forecast: parseEconomicNumber(r.forecast),
        }))
        .filter(p => !(Number.isNaN(p.actual) && Number.isNaN(p.forecast)))
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      setChartData(points);
    } catch (e) {
      setChartError(e.message || "Failed to load chart data");
    } finally {
      setChartLoading(false);
    }
  };

  const currencyOptions = [
    "USD","EUR","GBP","JPY","AUD","CAD","CHF","NZD","CNY","SEK","NOK"
  ];
  const impactOptions = ["Low", "Medium", "High"];
  const tableColumns = '5.25rem 3.25rem 5.25rem minmax(0,1fr) 4.25rem 4.25rem 4.25rem 2.5rem';
  const widgetControlButtonClassName = 'rounded-md border widget-muted-border bg-backgroundSurface/35 px-2.5 text-[11px] font-medium text-textSecondary transition hover:bg-secondary/10 hover:text-textPrimary';
  const widgetIconButtonClassName = 'h-8 w-8 shrink-0 rounded-md border widget-muted-border bg-backgroundSurface/35 p-0 text-textSecondary transition hover:bg-secondary/10 hover:text-textPrimary';

  const toggleInArray = (arr, setArr, value) => {
    if (arr.includes(value)) setArr(arr.filter(v => v !== value));
    else setArr([...arr, value]);
  };

  return (
    <Widget title={title} hideTitle={hideTitle} className="w-full">
      <div className="flex h-full min-h-0 flex-col gap-3">
        <div className={`flex items-center gap-2 shrink-0 ${hideTitle ? 'justify-between' : 'justify-end'}`}>
          {hideTitle && (
            <div className="inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] font-medium text-textSecondary">
              {totalCount} events
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchData}
              className={widgetIconButtonClassName}
              aria-label="Refresh economic calendar"
              title="Refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            </Button>
          {showViewAllLink && (
            <a
              href="/fundamentals/economic-calendar"
              className="inline-flex items-center rounded-md border widget-muted-border bg-backgroundSurface/30 px-2.5 py-1.5 text-[11px] font-medium text-primary transition hover:border-primary hover:bg-primary/10 hover:text-primaryHover"
            >
              View all
            </a>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="rounded-xl border widget-muted-border bg-backgroundSurface/20 p-2.5 shrink-0">
          <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-4'} gap-2`}>
            <Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="h-9" />
            <Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="h-9" />
            {!compact && (
              <div className="col-span-2">
                <Input placeholder="Search event" value={search} onChange={e=>setSearch(e.target.value)} className="h-9" />
              </div>
            )}
            <div className="col-span-2 flex flex-wrap gap-1">
              {impactOptions.map(i => (
                <Button key={i} size="sm" type="button" variant={impacts.includes(i) ? 'default' : 'outline'} onClick={()=>toggleInArray(impacts, setImpacts, i)} className="px-2.5">
                  {i}
                </Button>
              ))}
            </div>
            {!compact && (
              <div className="col-span-2 flex flex-wrap gap-1">
                {currencyOptions.map(c => (
                  <Button key={c} size="sm" type="button" variant={currencies.includes(c) ? 'default' : 'outline'} onClick={()=>toggleInArray(currencies, setCurrencies, c)} className="px-2.5">
                    {c}
                  </Button>
                ))}
              </div>
            )}
            <div className="col-span-2">
              <Button className="w-full" onClick={fetchData}>Apply</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-hidden rounded-xl border widget-muted-border bg-backgroundSurface/20">
        <div className="grid border-b widget-divider-border widget-header-fill px-3 py-2 text-[10px] font-medium uppercase tracking-[0.06em] text-textSecondary" style={{ gridTemplateColumns: tableColumns }}>
          <div>Date/Time</div>
          <div>Ccy</div>
          <div>Impact</div>
          <div>Event</div>
          <div className="text-right">Actual</div>
          <div className="text-right">Fcst</div>
          <div className="text-right">Prev</div>
          <div></div>
        </div>
        {loading && <div className="p-4 text-sm text-textSecondary">Loading...</div>}
        {error && !loading && <div className="m-3 rounded-lg border border-destructive/25 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
        {!loading && !error && events.length === 0 && (
          <div className="p-4 text-sm text-textSecondary">No events in this range.</div>
        )}
        {!loading && !error && (
          <div className="h-full overflow-y-auto">
            {events.map(ev => {
              const eventName = getEventName(ev);
              const impactMeta = IMPACT_META[ev.impact] || IMPACT_META.default;

              return (
                <div key={ev.id || `${ev.event_time}-${eventName}-${ev.currency}`} className="grid items-center border-b widget-divider-border px-3 py-2.5 text-xs transition hover:bg-secondary/10 last:border-b-0" style={{ gridTemplateColumns: tableColumns }}>
                  <div className="text-[11px] text-textSecondary">{formatDateTimeUtc(ev.event_time)}</div>
                  <div>
                    <span className="inline-flex rounded-md border widget-muted-border bg-backgroundSurface/40 px-1.5 py-0.5 text-[10px] font-medium text-textPrimary">{ev.currency}</span>
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${impactMeta.badgeClassName}`}>
                      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: impactMeta.color }} />
                      {ev.impact}
                    </span>
                  </div>
                  <div className="truncate text-xs font-medium text-textPrimary" title={eventName}>{eventName}</div>
                  <div className="text-right font-mono text-[11px] text-textPrimary" title={ev.actual ?? '-'}>{fmtVal(ev.actual)}</div>
                  <div className="text-right font-mono text-[11px] text-textSecondary"><CalendarForecastValue event={ev} /></div>
                  <div className="text-right font-mono text-[11px] text-textSecondary" title={ev.previous ?? '-'}>{fmtVal(ev.previous)}</div>
                  <div className="flex justify-end">
                    {showChart && ev.previous ? (
                      <button
                        className="inline-flex h-6 w-6 items-center justify-center rounded-md border widget-muted-border bg-backgroundSurface/35 text-primary transition hover:border-primary hover:bg-primary/10 hover:text-primaryHover"
                        onClick={() => openChart(eventName, ev.currency, ev.metadata?.mt5_calendar?.event_id)}
                        title="Open event chart"
                      >
                        ↗
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {limit > 0 && totalCount > 0 && (
        <div className="flex items-center justify-between gap-2 border-t widget-divider-border pt-3 text-xs text-textSecondary shrink-0">
          <span>
            {pageStart + 1}-{Math.min(pageEnd, totalCount)} of {totalCount}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={page <= 1 || loading}
              onClick={() => goToPage(page - 1)}
              className={widgetControlButtonClassName}
            >
              Prev
            </Button>
            <span>
              Page {page} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= totalPages || loading}
              onClick={() => goToPage(page + 1)}
              className={widgetControlButtonClassName}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {showChart && (
        <Modal open={chartOpen} onClose={() => setChartOpen(false)} title={chartTitle}>
          {chartLoading && <div className="p-4 text-textSecondary">Loading...</div>}
          {chartError && !chartLoading && <div className="p-4 text-destructive">{chartError}</div>}
          {!chartLoading && !chartError && chartData.length === 0 && (
            <div className="p-4 text-textSecondary">No data for the past 12 months.</div>
          )}
          {!chartLoading && !chartError && chartData.length > 0 && (
            <div className="h-80 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" strokeOpacity={0.35} />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} axisLine={{ stroke: 'var(--color-border-default)', opacity: 0.35 }} tickLine={{ stroke: 'var(--color-border-default)', opacity: 0.35 }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} domain={["auto", "auto"]} axisLine={{ stroke: 'var(--color-border-default)', opacity: 0.35 }} tickLine={{ stroke: 'var(--color-border-default)', opacity: 0.35 }} />
                  <Tooltip
                    formatter={(v) => (typeof v === 'number' ? v.toFixed(2) : v)}
                    contentStyle={{
                      backgroundColor: 'var(--color-background-surface)',
                      border: '1px solid var(--color-border-default)',
                      borderRadius: '0.75rem',
                      color: 'var(--color-text-primary)',
                    }}
                    labelStyle={{ color: 'var(--color-text-secondary)' }}
                  />
                  <Line type="monotone" dataKey="actual" name="Actual" stroke="var(--color-success)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="forecast" name="Forecast" stroke="var(--color-primary)" strokeWidth={2} dot={false} strokeDasharray="4 4" strokeOpacity={0.75} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Modal>
      )}
      </div>
    </Widget>
  );
}
